import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { FileData } from "../../types";
import { getThumbnailSignedUrl } from "../api/files";
import {
  ensureFullGcsPathForVse,
  ensureViewrPathStructure,
  isPlaceholderProjectThumbnail,
} from "../../utils/filePathUtils";
import {
  buildOptimisticViewRFileFromGcsPath,
  mergeOptimisticFinalFile,
} from "../../utils/viewrRecentFiles";
import {
  getFileKey,
  isFileBookmarkedForDisplay,
  orderFilesWithBookmarksFirst,
} from "../../utils/recentFileUtils";
import { fileQueryKeys } from "./fileQueryKeys";
import { traceProjectThumbnail } from "../../utils/thumbnailTrace";
import { appendCacheBusterIfUnsigned } from "../../utils/signedUrlUtils";
import {
  getFinalFileThumbnailUrl,
  normalizeFinalFile,
} from "../../utils/finalFileThumbnails";

type FileListUpdater = (files: FileData[]) => FileData[];
type FinalFileMatcher = (file: FileData) => boolean;
type FinalFilePatch = (file: FileData) => FileData;

const EMPTY_FILES: FileData[] = [];
const finalFilesPrefixQuery = (uid: string | null | undefined) =>
  uid == null ? (["finalFiles"] as const) : fileQueryKeys.finalFilesPrefix(uid);
const sampleFinalFilesQuery = (uid: string | null | undefined) =>
  uid == null ? (["sampleFinalFiles"] as const) : fileQueryKeys.sampleFinalFiles(uid);
const rawFilesQuery = (uid: string | null | undefined) =>
  uid == null ? (["rawFiles"] as const) : fileQueryKeys.rawFiles(uid);
const convertedFilesQuery = (uid: string | null | undefined) =>
  uid == null ? (["convertedFiles"] as const) : fileQueryKeys.convertedFiles(uid);
const trashFilesQuery = (uid: string | null | undefined) =>
  uid == null ? (["trashFiles"] as const) : fileQueryKeys.trashFiles(uid);
const libraryItemsQuery = (uid: string | null | undefined) =>
  uid == null ? (["libraryItems"] as const) : fileQueryKeys.libraryItems(uid);
const storageUsageQuery = (uid: string | null | undefined) =>
  uid == null ? (["storageUsage"] as const) : fileQueryKeys.storageUsage(uid);

const isDateCreatedSort = (queryKey: QueryKey): boolean =>
  queryKey[2] === "dateCreated" || queryKey[2] === "date_created";

const reorderFinalFiles = (queryKey: QueryKey, files: FileData[]): FileData[] =>
  orderFilesWithBookmarksFirst(
    files,
    isDateCreatedSort(queryKey) ? "dateCreated" : "lastViewed",
    (key) => {
      const row = files.find((file) => getFileKey(file) === key);
      return row ? isFileBookmarkedForDisplay(row) : false;
    },
  );

const updateQueryFileList = (
  queryClient: QueryClient,
  queryKey: QueryKey,
  updater: FileListUpdater,
) => {
  const previous = queryClient.getQueryData<FileData[]>(queryKey) ?? EMPTY_FILES;
  const next = updater(previous);
  queryClient.setQueryData(queryKey, next);
};

export const patchFinalFileCaches = ({
  queryClient,
  uid,
  matcher,
  patch,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  matcher: FinalFileMatcher;
  patch: FinalFilePatch;
}) => {
  const entries = queryClient.getQueriesData<FileData[]>({
    queryKey: finalFilesPrefixQuery(uid),
    exact: false,
  });

  for (const [queryKey, cachedFiles] of entries) {
    const files = cachedFiles ?? EMPTY_FILES;
    if (!files.length) continue;

    let changed = false;
    const next = files.map((file) => {
      if (!matcher(file)) return file;
      changed = true;
      return normalizeFinalFile(patch(file));
    });

    if (!changed) continue;
    queryClient.setQueryData(queryKey, reorderFinalFiles(queryKey, next));
  }
};

export const patchFinalFileAcrossSortCaches = patchFinalFileCaches;

export const removeFinalFileFromCaches = ({
  queryClient,
  uid,
  matcher,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  matcher: FinalFileMatcher;
}) => {
  const entries = queryClient.getQueriesData<FileData[]>({
    queryKey: finalFilesPrefixQuery(uid),
    exact: false,
  });

  for (const [queryKey, cachedFiles] of entries) {
    const files = cachedFiles ?? EMPTY_FILES;
    if (!files.length) continue;
    const next = files.filter((file) => !matcher(file));
    if (next.length === files.length) continue;
    queryClient.setQueryData(queryKey, reorderFinalFiles(queryKey, next));
  }
};

export const removeFinalFileAcrossSortCaches = removeFinalFileFromCaches;

export const upsertFinalFileInCaches = ({
  queryClient,
  uid,
  file,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  file: FileData;
}) => {
  const normalized = normalizeFinalFile(file);
  const entries = queryClient.getQueriesData<FileData[]>({
    queryKey: finalFilesPrefixQuery(uid),
    exact: false,
  });

  if (!entries.length) {
    queryClient.setQueryData(
      fileQueryKeys.finalFiles(uid),
      reorderFinalFiles(fileQueryKeys.finalFiles(uid), [normalized]),
    );
    return;
  }

  for (const [queryKey, cachedFiles] of entries) {
    const next = mergeOptimisticFinalFile(cachedFiles ?? EMPTY_FILES, normalized);
    queryClient.setQueryData(queryKey, reorderFinalFiles(queryKey, next));
  }
};

export const upsertOptimisticRecentViewR = upsertFinalFileInCaches;

export const patchTrashFilesCache = ({
  queryClient,
  uid,
  updater,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  updater: FileListUpdater;
}) => {
  updateQueryFileList(queryClient, trashFilesQuery(uid), (files) =>
    updater(files).map(normalizeFinalFile),
  );
};

export const patchTrashFiles = patchTrashFilesCache;

const isSameFinalFile = (left: FileData, right: FileData): boolean => {
  if (left.id && right.id && String(left.id) === String(right.id)) {
    return true;
  }

  const leftPaths = [left.gcs_path, left.full_path, left.path].filter(Boolean);
  const rightPaths = new Set(
    [right.gcs_path, right.full_path, right.path].filter(Boolean),
  );
  return leftPaths.some((path) => rightPaths.has(path));
};

/** Carry the last Recent thumbnail into Trash until the moved-path refetch arrives. */
export const upsertTrashFileInCache = ({
  queryClient,
  uid,
  file,
  movedAt = new Date().toISOString(),
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  file: FileData;
  movedAt?: string;
}): void => {
  const thumbnailUrl = getFinalFileThumbnailUrl(file);
  const movedFile = normalizeFinalFile({
    ...file,
    moved_to_trash_at: movedAt,
    ...(thumbnailUrl
      ? {
          thumbnailUrl,
          thumbnail_url: thumbnailUrl,
          thumbnail: thumbnailUrl,
        }
      : {}),
  });

  patchTrashFilesCache({
    queryClient,
    uid,
    updater: (files) => [
      movedFile,
      ...files.filter((candidate) => !isSameFinalFile(candidate, movedFile)),
    ],
  });
};

export const patchLibraryItemsCache = <T>({
  queryClient,
  uid,
  updater,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  updater: (items: T[]) => T[];
}) => {
  const queryKey = fileQueryKeys.libraryItems(uid);
  if (uid == null) {
    const previous = queryClient.getQueryData<T[]>(["libraryItems"]) ?? [];
    queryClient.setQueryData(["libraryItems"], updater(previous));
    return;
  }
  const previous = queryClient.getQueryData<T[]>(queryKey) ?? [];
  queryClient.setQueryData(queryKey, updater(previous));
};

export const patchLibraryItems = patchLibraryItemsCache;

export const invalidateFinalFiles = ({
  queryClient,
  uid,
  includeSamples = false,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  includeSamples?: boolean;
}) =>
  invalidateFileListQueries({
    queryClient,
    uid,
    includeSamples,
  });

const finalFileExistsInCaches = ({
  queryClient,
  uid,
  gcsPath,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  gcsPath: string;
}): boolean => {
  const canonical = canonicalViewrGcsPathForThumbnailCache(gcsPath);
  if (!canonical) {
    return false;
  }

  const entries = queryClient.getQueriesData<FileData[]>({
    queryKey: finalFilesPrefixQuery(uid),
    exact: false,
  });

  return entries.some(([, files]) =>
    (files ?? EMPTY_FILES).some((file) =>
      matchesPersistedViewrFilePath(file, canonical),
    ),
  );
};

/**
 * Home에 즉시 재사용할 수 있는 final-file 목록 snapshot이 있는지 확인한다.
 * query entry만 생성되고 아직 응답 데이터가 없는 초기 loading 상태는 snapshot으로 보지 않는다.
 */
const hasReusableFinalFilesSnapshot = ({
  queryClient,
  uid,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
}): boolean =>
  queryClient
    .getQueriesData<FileData[]>({
      queryKey: finalFilesPrefixQuery(uid),
      exact: false,
    })
    .some(([, files]) => Array.isArray(files));

/** Home·Editor 복귀 직전에 ViewR row가 캐시에 없으면 optimistic으로 선반영한다. */
export const ensureViewrInFinalFileCaches = ({
  queryClient,
  uid,
  gcsPath,
  title,
  downloadUrl,
  createdAt,
  projectId,
  storageMode,
  editHeadRevisionId,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  gcsPath?: string | null;
  title?: string;
  downloadUrl?: string;
  createdAt?: string;
  projectId?: string | number;
  storageMode?: FileData["storage_mode"];
  editHeadRevisionId?: string | null;
}): void => {
  const canonical = canonicalViewrGcsPathForThumbnailCache(gcsPath);
  if (!canonical || !uid) {
    return;
  }

  const touchedAt = createdAt || new Date().toISOString();
  const normalizedTitle = title?.trim() || "";
  if (finalFileExistsInCaches({ queryClient, uid, gcsPath: canonical })) {
    patchFinalFileCaches({
      queryClient,
      uid,
      matcher: (file) => matchesPersistedViewrFilePath(file, canonical),
      patch: (file) => {
        const next: FileData = {
          ...file,
          updated_at: touchedAt,
          updated: touchedAt,
          lastEdited: touchedAt,
        };
        if (normalizedTitle) {
          const displayFileName = normalizedTitle.toLowerCase().endsWith(".viewr")
            ? normalizedTitle
            : `${normalizedTitle}.viewr`;
          next.title = normalizedTitle.replace(/\.viewr$/i, "") || normalizedTitle;
          next.name = displayFileName;
          next.filename = displayFileName;
          next.file_name = displayFileName;
        }
        if (projectId != null) next.id = String(projectId);
        if (storageMode) next.storage_mode = storageMode;
        if (editHeadRevisionId !== undefined) {
          next.edit_head_revision_id = editHeadRevisionId;
        }
        return next;
      },
    });
    return;
  }

  upsertFinalFileInCaches({
    queryClient,
    uid,
    file: {
      ...buildOptimisticViewRFileFromGcsPath(canonical, {
        title,
        downloadUrl,
        createdAt: touchedAt,
      }),
      ...(projectId != null ? { id: String(projectId) } : {}),
      ...(storageMode ? { storage_mode: storageMode } : {}),
      ...(editHeadRevisionId !== undefined
        ? { edit_head_revision_id: editHeadRevisionId }
        : {}),
    },
  });
};

/** Create 완료 직후 Home 목록 캐시에 새 ViewR을 즉시 반영한다. */
/** Editor/Home rename 직후 final 목록 캐시의 표시명을 즉시 반영한다 (Policy A: gcs_path 유지). */
export const syncViewrRenameCompleted = ({
  queryClient,
  uid,
  gcsPath,
  displayName,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  gcsPath?: string | null;
  displayName?: string | null;
}): void => {
  const canonical = canonicalViewrGcsPathForThumbnailCache(gcsPath);
  const trimmed = displayName?.trim() || "";
  if (!canonical || !uid || !trimmed) {
    return;
  }

  const baseTitle = trimmed.replace(/\.viewr$/i, "") || trimmed;
  const fileName = trimmed.toLowerCase().endsWith(".viewr")
    ? trimmed
    : `${baseTitle}.viewr`;

  patchFinalFileCaches({
    queryClient,
    uid,
    matcher: (file) => matchesPersistedViewrFilePath(file, canonical),
    patch: (file) => ({
      ...file,
      title: baseTitle,
      name: fileName,
      filename: fileName,
      file_name: fileName,
      updated_at: new Date().toISOString(),
      updated: new Date().toISOString(),
      lastEdited: new Date().toISOString(),
    }),
  });
};

export const syncViewrCreationCompleted = ({
  queryClient,
  uid,
  gcsPath,
  title,
  downloadUrl,
  createdAt,
  projectId,
  storageMode,
  editHeadRevisionId,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  gcsPath?: string | null;
  title?: string;
  downloadUrl?: string;
  createdAt?: string;
  projectId?: string | number;
  storageMode?: FileData["storage_mode"];
  editHeadRevisionId?: string | null;
}): void => {
  ensureViewrInFinalFileCaches({
    queryClient,
    uid,
    gcsPath,
    title,
    downloadUrl,
    createdAt,
    projectId,
    storageMode,
    editHeadRevisionId,
  });
};

/** Home 진입·에디터 복귀 시 최신 썸네일 signed URL을 받기 위해 final 목록을 강제 갱신한다. */
export const refreshHomeFinalFilesList = async ({
  queryClient,
  uid,
  includeSamples = true,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  includeSamples?: boolean;
}): Promise<void> => {
  invalidateFinalFiles({ queryClient, uid, includeSamples });
  await queryClient.refetchQueries({
    queryKey: finalFilesPrefixQuery(uid),
    type: "all",
  });
  if (includeSamples) {
    await queryClient.refetchQueries({
      queryKey: sampleFinalFilesQuery(uid),
      type: "all",
    });
  }
};

export const invalidateLibrary = ({
  queryClient,
  uid,
  includeConverted = true,
  includeStorageUsage = false,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  includeConverted?: boolean;
  includeStorageUsage?: boolean;
}) =>
  invalidateFileListQueries({
    queryClient,
    uid,
    includeLibrary: true,
    includeConverted,
    includeStorageUsage,
  });

export const invalidateTrash = ({
  queryClient,
  uid,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
}) =>
  invalidateFileListQueries({
    queryClient,
    uid,
    includeTrash: true,
  });

export const invalidateStorageUsage = ({
  queryClient,
  uid,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
}) =>
  invalidateFileListQueries({
    queryClient,
    uid,
    includeStorageUsage: true,
  });

export const canonicalViewrGcsPathForThumbnailCache = (
  path: string | null | undefined,
): string => {
  const structured = ensureViewrPathStructure(path);
  return ensureFullGcsPathForVse(structured) || structured;
};

const matchesPersistedViewrFilePath = (
  file: FileData,
  gcsPath: string,
): boolean => {
  const canonical = canonicalViewrGcsPathForThumbnailCache(gcsPath);
  if (!canonical) {
    return false;
  }

  const candidates = [file.gcs_path, file.full_path, file.path].filter(
    (value): value is string => typeof value === "string" && value.length > 0,
  );

  return candidates.some(
    (candidate) =>
      canonicalViewrGcsPathForThumbnailCache(candidate) === canonical,
  );
};

export const invalidateFileListQueries = ({
  queryClient,
  uid,
  includeRaw = false,
  includeConverted = false,
  includeTrash = false,
  includeLibrary = false,
  includeSamples = false,
  includeStorageUsage = false,
  skipFinalFiles = false,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  includeRaw?: boolean;
  includeConverted?: boolean;
  includeTrash?: boolean;
  includeLibrary?: boolean;
  includeSamples?: boolean;
  includeStorageUsage?: boolean;
  skipFinalFiles?: boolean;
}) => {
  if (!skipFinalFiles) {
    void queryClient.invalidateQueries({
      queryKey: finalFilesPrefixQuery(uid),
      exact: false,
    });
  }

  if (includeRaw) {
    void queryClient.invalidateQueries({ queryKey: rawFilesQuery(uid) });
  }
  if (includeConverted) {
    void queryClient.invalidateQueries({ queryKey: convertedFilesQuery(uid) });
  }
  if (includeTrash) {
    void queryClient.invalidateQueries({ queryKey: trashFilesQuery(uid) });
  }
  if (includeLibrary) {
    void queryClient.invalidateQueries({ queryKey: libraryItemsQuery(uid) });
  }
  if (includeSamples) {
    void queryClient.invalidateQueries({ queryKey: sampleFinalFilesQuery(uid) });
  }
  if (includeStorageUsage) {
    void queryClient.invalidateQueries({ queryKey: storageUsageQuery(uid) });
  }
};

export const syncFileConversionCompletion = ({
  queryClient,
  uid,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
}) => {
  invalidateFileListQueries({
    queryClient,
    uid,
    includeRaw: true,
    includeConverted: true,
    includeLibrary: true,
  });
};

export const syncViewrUpdateCompletion = ({
  queryClient,
  uid,
  gcsPath,
  title,
  downloadUrl,
  createdAt,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  gcsPath?: string | null;
  title?: string;
  downloadUrl?: string;
  createdAt?: string;
}) => {
  if (gcsPath) {
    syncViewrCreationCompleted({
      queryClient,
      uid,
      gcsPath,
      title,
      downloadUrl,
      createdAt,
    });
    invalidateFileListQueries({
      queryClient,
      uid,
      includeLibrary: true,
      skipFinalFiles: true,
    });
    return;
  }

  invalidateFileListQueries({ queryClient, uid });
};

const resolveBrowserAuthToken = (): string | null =>
  (typeof window !== "undefined" &&
    (localStorage.getItem("firebaseToken") ||
      sessionStorage.getItem("firebaseToken"))) ||
  null;

/** sidecar 동기화·Home refetch race를 흡수하기 위한 재시도 간격(ms) */
export const PROJECT_THUMBNAIL_CACHE_REFRESH_DELAYS_MS = [
  0, 400, 900, 1800, 3500,
] as const;

const RECENT_SAVE_THUMBNAIL_TTL_MS = 120_000;

type ThumbnailRefreshSchedule = {
  attemptIndex: number;
  timer?: ReturnType<typeof setTimeout>;
};

const thumbnailRefreshSchedulesByPath = new Map<
  string,
  ThumbnailRefreshSchedule
>();
const recentSavePersistedAtByPath = new Map<string, number>();

const clearScheduledThumbnailRefresh = (gcsPath: string): void => {
  const schedule = thumbnailRefreshSchedulesByPath.get(gcsPath);
  if (schedule?.timer !== undefined) {
    clearTimeout(schedule.timer);
  }
  thumbnailRefreshSchedulesByPath.delete(gcsPath);
};

export const noteRecentSavePersistedForThumbnail = (gcsPath: string): void => {
  const canonical = canonicalViewrGcsPathForThumbnailCache(gcsPath);
  if (!canonical) {
    return;
  }
  const now = Date.now();
  recentSavePersistedAtByPath.set(canonical, now);
  recentSavePersistedAtByPath.forEach((savedAt, path) => {
    if (now - savedAt > RECENT_SAVE_THUMBNAIL_TTL_MS) {
      recentSavePersistedAtByPath.delete(path);
    }
  });
};

export const isRecentlySavedViewrPath = (gcsPath: string): boolean => {
  const canonical = canonicalViewrGcsPathForThumbnailCache(gcsPath);
  const savedAt = canonical
    ? recentSavePersistedAtByPath.get(canonical)
    : undefined;
  return (
    typeof savedAt === "number" &&
    Date.now() - savedAt <= RECENT_SAVE_THUMBNAIL_TTL_MS
  );
};

/** Save 직후 .viewr bundle → sidecar 반영된 Home 카드 썸네일을 캐시에 반영합니다. placeholder는 패치하지 않습니다. */
export const patchProjectThumbnailAfterSave = async ({
  queryClient,
  uid,
  gcsPath,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  gcsPath?: string | null;
}): Promise<boolean> => {
  const canonicalPath = canonicalViewrGcsPathForThumbnailCache(gcsPath);
  if (!canonicalPath) {
    return false;
  }

  const token = resolveBrowserAuthToken();
  if (!token) {
    return false;
  }

  try {
    const response = (await getThumbnailSignedUrl(canonicalPath, token)) as {
      success?: boolean;
      signed_url?: string;
      thumbnail_path?: string;
    } | null;
    if (!response?.success || !response.signed_url) {
      return false;
    }

    if (
      isPlaceholderProjectThumbnail(
        response.signed_url,
        response.thumbnail_path,
      )
    ) {
      return false;
    }

    const versionedUrl = appendCacheBusterIfUnsigned(response.signed_url);

    patchFinalFileCaches({
      queryClient,
      uid,
      matcher: (file) => matchesPersistedViewrFilePath(file, canonicalPath),
      patch: (file) => ({
        ...file,
        thumbnailUrl: versionedUrl,
        thumbnail_url: versionedUrl,
        thumbnail: versionedUrl,
      }),
    });
    return true;
  } catch (error) {
    traceProjectThumbnail("patch_failed", {
      gcsPath: canonicalPath,
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

/** Save 직후 sidecar 준비 지연·즉시 Home 이탈 race를 흡수하는 썸네일 캐시 갱신 스케줄러 */
export const scheduleProjectThumbnailCacheRefresh = ({
  queryClient,
  uid,
  gcsPath,
  restart = false,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  gcsPath: string;
  /** 새 저장 완료는 새 5회 확인 구간을 시작하고, Home 재진입은 기존 구간을 재사용한다. */
  restart?: boolean;
}): void => {
  const canonicalPath = canonicalViewrGcsPathForThumbnailCache(gcsPath);
  if (!canonicalPath) {
    return;
  }

  noteRecentSavePersistedForThumbnail(canonicalPath);
  const existingSchedule = thumbnailRefreshSchedulesByPath.get(canonicalPath);
  if (existingSchedule && !restart) {
    return;
  }

  if (existingSchedule) {
    clearScheduledThumbnailRefresh(canonicalPath);
  }

  const schedule: ThumbnailRefreshSchedule = { attemptIndex: 0 };
  thumbnailRefreshSchedulesByPath.set(canonicalPath, schedule);
  const runAttempt = (): void => {
    if (thumbnailRefreshSchedulesByPath.get(canonicalPath) !== schedule) {
      return;
    }

    // 기존 sidecar가 있으면 첫 요청도 성공하므로 성공 여부만으로 중단하면
    // backend가 새 thumbnail.jpeg를 반영하기 전에 stale URL로 고정됩니다.
    // 전체 짧은 재확인 구간을 수행해 갱신된 sidecar를 다시 캐시에 반영합니다.
    void patchProjectThumbnailAfterSave({
      queryClient,
      uid,
      gcsPath: canonicalPath,
    });

    schedule.attemptIndex += 1;
    if (schedule.attemptIndex >= PROJECT_THUMBNAIL_CACHE_REFRESH_DELAYS_MS.length) {
      thumbnailRefreshSchedulesByPath.delete(canonicalPath);
      return;
    }

    const delay =
      PROJECT_THUMBNAIL_CACHE_REFRESH_DELAYS_MS[schedule.attemptIndex] -
      PROJECT_THUMBNAIL_CACHE_REFRESH_DELAYS_MS[schedule.attemptIndex - 1];
    schedule.timer = setTimeout(runAttempt, delay);
  };

  runAttempt();
};

/** Home 진입·목록 refetch 직후 최근 Save 프로젝트 썸네일을 한 번 더 동기화 */
export const refreshRecentSaveThumbnailsAfterHomeListSync = async ({
  queryClient,
  uid,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
}): Promise<void> => {
  const now = Date.now();
  const paths = Array.from(recentSavePersistedAtByPath.entries())
    .filter(([, savedAt]) => now - savedAt <= RECENT_SAVE_THUMBNAIL_TTL_MS)
    .map(([path]) => path);

  for (const gcsPath of paths) {
    scheduleProjectThumbnailCacheRefresh({ queryClient, uid, gcsPath });
  }
};

export const EDITOR_EXIT_THUMBNAIL_REFRESH_EVENT =
  "viewr:editorExitThumbnailRefresh";

let rememberedEditorExitViewrGcsPath: string | null = null;

/** Editor → Home 직전에 열려 있던 ViewR 경로를 기억한다. */
export const rememberEditorExitViewrGcsPath = (
  path: string | null | undefined,
): void => {
  rememberedEditorExitViewrGcsPath =
    canonicalViewrGcsPathForThumbnailCache(path) || null;
};

export const resolveEditorExitViewrGcsPath = (
  explicitPath?: string | null,
): string | null => {
  const fromExplicit = canonicalViewrGcsPathForThumbnailCache(explicitPath);
  if (fromExplicit) {
    return fromExplicit;
  }
  return rememberedEditorExitViewrGcsPath;
};

export const consumeEditorExitViewrGcsPath = (): string | null => {
  const path = rememberedEditorExitViewrGcsPath;
  rememberedEditorExitViewrGcsPath = null;
  return path;
};

/** 명시적 Save가 확정된 ViewR만 Editor 종료 시 Cloud thumbnail API로 재동기화한다. */
export const refreshEditorExitViewrFromCloud = async ({
  queryClient,
  uid,
  gcsPath: explicitPath,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  gcsPath?: string | null;
}): Promise<void> => {
  const gcsPath =
    canonicalViewrGcsPathForThumbnailCache(explicitPath) ||
    consumeEditorExitViewrGcsPath();
  if (!gcsPath || !isRecentlySavedViewrPath(gcsPath)) {
    return;
  }

  // 새 sidecar URL을 확인하기 전까지 기존 카드 썸네일을 유지해 placeholder 깜빡임을 막습니다.
  scheduleProjectThumbnailCacheRefresh({ queryClient, uid, gcsPath });

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(EDITOR_EXIT_THUMBNAIL_REFRESH_EVENT, {
        detail: { gcsPath },
      }),
    );
  }
};

/** Home navigation 시 목록 refetch + editor exit ViewR cloud thumbnail refresh. */
export const runHomeNavigationFinalFilesRefresh = async ({
  queryClient,
  uid,
  viewrGcsPath,
  includeSamples = true,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  viewrGcsPath?: string | null;
  includeSamples?: boolean;
}): Promise<void> => {
  traceProjectThumbnail("home_refresh_started", {
    gcsPath: viewrGcsPath,
  });
  // 전체 목록 snapshot이 GC된 상태에서 현재 파일 1건만 새 캐시로 만들면
  // Home이 `1건 -> 전체 목록`으로 깜빡인다. 재사용 가능한 목록이 있을 때만 선반영한다.
  if (hasReusableFinalFilesSnapshot({ queryClient, uid })) {
    ensureViewrInFinalFileCaches({
      queryClient,
      uid,
      gcsPath: viewrGcsPath,
    });
  }
  await refreshHomeFinalFilesList({ queryClient, uid, includeSamples });
  await refreshEditorExitViewrFromCloud({
    queryClient,
    uid,
    gcsPath: viewrGcsPath,
  });
  await refreshRecentSaveThumbnailsAfterHomeListSync({ queryClient, uid });
  traceProjectThumbnail("home_refresh_completed", {
    gcsPath: viewrGcsPath,
  });
};

export const syncViewrSavePersisted = ({
  queryClient,
  uid,
  gcsPath,
  affectsLibrary = true,
  skipThumbnailRefresh = false,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  gcsPath?: string | null;
  affectsLibrary?: boolean;
  skipThumbnailRefresh?: boolean;
}) => {
  const editedAt = new Date().toISOString();
  const canonicalPath = canonicalViewrGcsPathForThumbnailCache(gcsPath);
  const shouldRefreshThumbnail = Boolean(canonicalPath) && !skipThumbnailRefresh;

  if (canonicalPath) {
    patchFinalFileCaches({
      queryClient,
      uid,
      matcher: (file) => matchesPersistedViewrFilePath(file, canonicalPath),
      patch: (file) => ({
        ...file,
        updated_at: editedAt,
        updated: editedAt,
        lastEdited: editedAt,
      }),
    });
    if (shouldRefreshThumbnail) {
      scheduleProjectThumbnailCacheRefresh({
        queryClient,
        uid,
        gcsPath: canonicalPath,
        restart: true,
      });
    }
  }

  invalidateFileListQueries({
    queryClient,
    uid,
    includeLibrary: affectsLibrary,
    // 저장된 row의 시간 정보는 위에서 patch합니다. finalFiles refetch는 기존 썸네일을
    // null/stale 응답으로 덮을 수 있으므로 manual Save와 autosave 모두 여기서는 유지합니다.
    skipFinalFiles: Boolean(canonicalPath),
  });
};

export const syncLibraryDeletion = ({
  queryClient,
  uid,
  libraryItemId,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  libraryItemId?: string;
}) => {
  if (libraryItemId) {
    queryClient.removeQueries({
      queryKey: fileQueryKeys.libraryDetail(uid, libraryItemId),
    });
  }

  invalidateFileListQueries({
    queryClient,
    uid,
    includeLibrary: true,
    includeConverted: true,
    includeStorageUsage: true,
  });
};

export const applyFinalFileBookmarkPatch = ({
  queryClient,
  uid,
  matcher,
  bookmarked,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  matcher: FinalFileMatcher;
  bookmarked: boolean;
}) => {
  const touchedAt = new Date().toISOString();
  patchFinalFileCaches({
    queryClient,
    uid,
    matcher,
    patch: (file) => ({
      ...file,
      is_bookmarked: bookmarked,
      bookmarked_at: bookmarked ? touchedAt : null,
    }),
  });
};

export const applyBookmarkPatch = applyFinalFileBookmarkPatch;

export const applyFinalFileRenamePatch = ({
  queryClient,
  uid,
  matcher,
  fileName,
}: {
  queryClient: QueryClient;
  uid: string | null | undefined;
  matcher: FinalFileMatcher;
  fileName: string;
}) => {
  patchFinalFileCaches({
    queryClient,
    uid,
    matcher,
    patch: (file) => ({
      ...file,
      name: fileName,
      filename: fileName,
      file_name: fileName,
      title: fileName,
    }),
  });
};

export const applyRenamePatch = applyFinalFileRenamePatch;

export const buildFileMatcher = ({
  projectId,
  primaryPath,
  alternatePath,
}: {
  projectId?: string | null;
  primaryPath?: string | null;
  alternatePath?: string | null;
}): FinalFileMatcher => {
  const projectRef = projectId ? String(projectId) : "";
  const pathA = primaryPath || "";
  const pathB = alternatePath || "";

  return (file) => {
    if (projectRef && file.id && String(file.id) === projectRef) return true;
    if (pathA && (file.gcs_path === pathA || file.full_path === pathA)) return true;
    if (pathB && (file.gcs_path === pathB || file.full_path === pathB)) return true;
    return false;
  };
};
